import { CVSData } from '@/lib/cvsdata/cvsdata';
import { ConsentIdTypeMap, ECEMailObj, GetCommunicationPreferencesResponse, TargetIdType } from '@/lib/types/datatypes';
import { isHealth100Client } from '@/lib/utils/utils';
import { useAlertProvider } from '@cvs-web/splash-components';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from '@/components/translation';

const useCommPrefsQuery = () => {
  const { setAlerts } = useAlertProvider();

  const failureTitle = useTranslation('error.failure.title');
  const failureMessage = useTranslation('error.failure.message');

  const query = useQuery({
    queryKey: ['communicationPreferences'],
    queryFn: async (): Promise<GetCommunicationPreferencesResponse> => {
      const commPrefsData = await CVSData.getCommPrefs();
      const {
        extracareAccessLevel,
        cardType = '',
        extracareCardNumber = '',
      } = commPrefsData?.data?.profile?.extraCareDetails || {};

      // Fetch and combine RX comm-prefs into comm-prefs array
      // TODO this is only needed until the backend consolidates preferences into one call
      // 09/16/2025 - This will be handled with linkedSSK's soon
      if (commPrefsData.data?.profile?.rxTied === 'Y') {
        const rxPrefData = await CVSData.getRxCommPrefs();
        const rxPreferences = rxPrefData?.data?.getCommunicationPreferences?.preferences?.[0];
        if (rxPreferences) {
          commPrefsData?.data?.preferences?.preferences?.push(rxPreferences);
        }
      }

      // Map consents to id types based on LOB
      const consentedIdTypes: TargetIdType[] = [];
      if (isHealth100Client()) { // I love it when a good plan comes together
        consentedIdTypes.push(
          ...Object
            .values(TargetIdType)
            .filter((type) => type !== TargetIdType.RETAIL_PROFILE_ID_TYPE)
        );
      } else {
        commPrefsData.data?.memberConsents?.consents.forEach((mc) => {
          mc.provisions.forEach((p) => {
            const mappedConsent = ConsentIdTypeMap[p.actions[0]!.key];
            if (p.type === 'allow' && mappedConsent) {
              consentedIdTypes.push(mappedConsent);
            }
          });
        });
      }

      // Fetch comm prefs for consented id types
      if (consentedIdTypes.length > 0) {
        const xlobPrefData = await CVSData.getXLoBCommPrefs(consentedIdTypes);
        if (commPrefsData.data) {
          commPrefsData.data!.linkedSSKs = xlobPrefData.data.linkedSSKs;
        }
      }

      // If EC-tied: perform another request for additional EC info and patch the info into the profile response
      if (extracareAccessLevel === 'P' && cardType && extracareCardNumber) {
        const ecData = await CVSData.getExtracareInfo(extracareCardNumber, cardType);
        const email = (ecData?.data?.ecCustProfile?.tables?.find((t) => t.tableName === 'customer_email')?.row?.[0] as ECEMailObj | undefined)
          ?.email_addr_txt;

        return {
          ...commPrefsData,
          data: {
            ...commPrefsData?.data,
            profile: {
              ...commPrefsData?.data?.profile,
              extraCareDetails: {
                ...commPrefsData?.data?.profile?.extraCareDetails,
                ecProfileEmail: email,
                directMailOptFlag: ecData?.data?.ecCustProfile?.xtraCarePrefs?.optInEc === 'I',
                emailOptFlag: ecData?.data?.ecCustProfile?.xtraCarePrefs?.optInEmail === 'A',
                digitalReceiptPref: ecData?.data?.ecCustProfile?.xtraCarePrefs?.digitalReceipt,
                ecSMSEnrolled: ecData?.data?.ecCustProfile?.xtraCarePrefs?.sms,
              },
            },
          },
        };
      }

      return commPrefsData;
    },
  });

  useEffect(() => {
    if (query.error) {
      setAlerts([
        {
          type: 'error',
          title: failureTitle,
          message: failureMessage,
        },
      ]);
    }
  }, [setAlerts, query.error]);

  return query;
};

export default useCommPrefsQuery;
